/**
 * ModelConsensusPanel.jsx
 * BerBul AI Trading — Shared Intelligence Component
 *
 * Shows the three-model vote breakdown for a PROCEED/SKIP decision:
 *   DRL  (Deep Reinforcement Learning) direction
 *   DT   (Decision Tree) direction
 *   Pattern recognition alignment
 *   Model leadership + consensus strength
 *   Timing sync signal
 *
 * Works in both contexts:
 *   TradingCommand       — pass liveBars; shows last evaluated bar
 *   DailyDetailDashboard — pass liveBars or a single bar via singleBar prop
 *
 * Props:
 *   liveBars   {Array}   — full bar array; component finds last meaningful bar
 *   singleBar  {Object}  — optional: override with a specific bar (e.g. from TradeCard)
 *   compact    {boolean} — compact 1-row layout (default false)
 *   showLabel  {boolean} — show "MODELS" section label (default true)
 *
 * No external dependencies. Pure BRD design system.
 */

import { useMemo } from "react";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      "#08101E",
  surface: "#0C1525",
  s2:      "#101B2E",
  s3:      "#0e1828",
  border:  "#1E2D42",
  b2:      "#2a3d56",
  text:    "#E8EDF5",
  muted:   "#6A7D95",
  body:    "#B8C8DC",
  green:   "#00E896",
  cyan:    "#00C8FF",
  amber:   "#FFB340",
  red:     "#FF4466",
  blue:    "#4488FF",
  purple:  "#9966FF",
};

const MONO = "'DM Mono', 'Courier New', monospace";

// ── Helpers ───────────────────────────────────────────────────────────────────
function dirColor(dir) {
  if (!dir) return C.muted;
  const d = dir.toLowerCase();
  if (d === "bullish" || d === "buy" || d === "long") return C.green;
  if (d === "bearish" || d === "sell" || d === "short") return C.red;
  return C.muted;
}

function dirArrow(dir) {
  if (!dir) return "—";
  const d = dir.toLowerCase();
  if (d === "bullish" || d === "buy" || d === "long") return "▲";
  if (d === "bearish" || d === "sell" || d === "short") return "▼";
  return "—";
}

function leaderColor(v) {
  return { chaotic: C.red, drl_leading: C.cyan, dt_leading: C.blue, aligned: C.green }[v] || C.muted;
}

function leaderLabel(v) {
  if (!v) return "UNKNOWN";
  return v.replace(/_/g, " ").toUpperCase();
}

function consensusColor(strength) {
  if (strength == null) return C.muted;
  if (strength >= 0.80) return C.green;
  if (strength >= 0.60) return C.cyan;
  if (strength >= 0.40) return C.amber;
  return C.red;
}

// ── Extractor ─────────────────────────────────────────────────────────────────
function extractModelData(bar) {
  if (!bar) return null;
  const story = bar.eval_agent_story;

  // Chart analysis → llm_full_response → model_intelligence (new schema)
  const llmFull = story?.chart_analysis?.llm_full_response || {};
  const mi      = llmFull.model_intelligence || {};
  const ra      = llmFull.regime_analysis || {};
  const ia      = llmFull.integrated_analysis || {};
  const fft     = llmFull.fft_lag_intelligence || {};

  // DRL direction: model_intelligence first, then raw action_drl
  const drlDir = mi.drl_trend_direction;
  const drlRaw = bar.action_drl;
  const drl = drlDir || (drlRaw === 2 ? "bullish" : drlRaw === 0 ? "bearish" : "neutral");

  // DT direction: model_intelligence first, then bar.trend
  const dtDir  = mi.dt_trend_direction;
  const trend  = bar.trend || "";
  const dt = dtDir || (trend === "BUY" ? "bullish" : trend === "SELL" ? "bearish" : "neutral");

  // Pattern direction: from patterns array or pattern_tag_analysis
  const patterns = Array.isArray(llmFull.identified_patterns) ? llmFull.identified_patterns : [];
  const legacyPatterns = Array.isArray(story?.patterns) ? story.patterns : [];
  const ptag = story?.pattern_tag_analysis;

  let patternDir = "neutral";
  if (patterns.length > 0) {
    // Use first pattern's trend_tags vs_position as proxy for alignment
    const p = patterns[0];
    const vsPos = p.trend_tags?.vs_position || p.tags?.position_alignment || "";
    if (vsPos.startsWith("aligned") || vsPos.startsWith("with") || vsPos.startsWith("bullish")) patternDir = "bullish";
    else if (vsPos.startsWith("against")) patternDir = "bearish";
  } else if (legacyPatterns.length > 0) {
    const p = legacyPatterns[0];
    if (p.tags?.position === "aligned") patternDir = "bullish";
    else if (p.tags?.position === "against") patternDir = "bearish";
  } else if (ptag) {
    if (ptag.direction === "BULLISH") patternDir = "bullish";
    else if (ptag.direction === "BEARISH") patternDir = "bearish";
  }

  // Pattern name
  const patternName = patterns[0]?.pattern_name || legacyPatterns[0]?.pattern || ptag?.pattern || null;
  const patternCount = patterns.length || legacyPatterns.length;

  // Model leadership
  const leadership = mi.model_leadership_pattern
    || story?.contradictions?.model_vs_pattern
    || (drl === dt ? "aligned" : "chaotic");

  // Consensus strength
  const consensusStrength = mi.model_consensus_strength ?? null;

  // Timing sync
  const timingSync = !!(ia.timing_convergence ?? fft.timing_convergence ?? fft.timing_sync ?? false);

  // Rule votes (decision tree of internal votes)
  const ruleVotes = story?.thinking_snapshots?.initial_thinking?.rule_votes || story?.rule_votes || null;

  // Evidence weights
  const weights = story?.thinking_snapshots?.initial_thinking?.weights || story?.weights || null;

  // Decision for context
  const ds = story?.decision || {};
  const decision = ds.final || story?.eval_decision || bar.eval_decision || bar.decision || "SKIP";

  // Timestamp
  const time = bar.t
    ? new Date(bar.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
    : null;

  return { drl, dt, patternDir, patternName, patternCount, leadership, consensusStrength, timingSync, ruleVotes, weights, decision, time };
}

// ── Model vote tile ───────────────────────────────────────────────────────────
function VoteTile({ label, dir, sublabel }) {
  const color = dirColor(dir);
  const arrow = dirArrow(dir);
  return (
    <div style={{
      flex: 1, background: C.s3, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "10px 8px",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    }}>
      <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: "1px" }}>
        {label}
      </span>
      <span style={{ fontSize: 20, color, lineHeight: 1 }}>{arrow}</span>
      <span style={{ fontFamily: MONO, fontSize: 9, color, fontWeight: 500 }}>
        {dir ? dir.toUpperCase() : "—"}
      </span>
      {sublabel && (
        <span style={{ fontFamily: MONO, fontSize: 7, color: C.muted, textAlign: "center", marginTop: 1 }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

// ── Weight bar row ────────────────────────────────────────────────────────────
function WeightRow({ label, value }) {
  if (value == null) return null;
  const pctVal = Math.round(value * 100);
  const color = pctVal >= 50 ? C.cyan : pctVal >= 30 ? C.body : C.muted;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, width: 64, flexShrink: 0, textTransform: "uppercase" }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pctVal}%`, height: "100%", background: color, borderRadius: 2, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontFamily: MONO, fontSize: 8, color, width: 28, textAlign: "right" }}>
        {pctVal}%
      </span>
    </div>
  );
}

// ── Vote dot (for ruleVotes display) ─────────────────────────────────────────
function VoteDots({ votes }) {
  if (!votes) return null;
  const entries = Object.entries(votes);
  if (entries.length === 0) return null;

  const proceed = entries.filter(([, v]) => v === "PROCEED" || v === true).length;
  const total   = entries.length;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>VOTES</span>
      {entries.slice(0, 6).map(([key, val]) => {
        const isYes = val === "PROCEED" || val === true || val === "BUY";
        return (
          <div key={key} title={`${key}: ${val}`} style={{
            width: 8, height: 8, borderRadius: "50%",
            background: isYes ? C.green : val === "SKIP" || val === false ? C.red : C.muted,
            border: `1px solid ${C.border}`,
            cursor: "help",
          }} />
        );
      })}
      <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
        {proceed}/{total}
      </span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ModelConsensusPanel({ liveBars = [], singleBar = null, compact = false, showLabel = true }) {

  const data = useMemo(() => {
    if (singleBar) return extractModelData(singleBar);

    const bars = (liveBars || []);
    // Prefer the last bar with model_intelligence in llm_full_response
    const withMI = bars.filter(b =>
      b.eval_agent_story?.chart_analysis?.llm_full_response?.model_intelligence
    );
    if (withMI.length > 0) return extractModelData(withMI[withMI.length - 1]);

    // Fallback: last bar with any agent story
    const withStory = bars.filter(b => b.eval_agent_story);
    if (withStory.length > 0) return extractModelData(withStory[withStory.length - 1]);

    // Last bar at all
    return bars.length > 0 ? extractModelData(bars[bars.length - 1]) : null;
  }, [liveBars, singleBar]);

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!data) {
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>
          No model data — waiting for evaluation
        </span>
      </div>
    );
  }

  const lColor = leaderColor(data.leadership);
  const cColor = consensusColor(data.consensusStrength);

  // ── Compact layout ───────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 8, padding: "8px 14px",
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      }}>
        {showLabel && (
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: "1.2px" }}>MODELS</span>
        )}

        {/* DRL */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>DRL</span>
          <span style={{ fontSize: 13, color: dirColor(data.drl) }}>{dirArrow(data.drl)}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: dirColor(data.drl) }}>
            {data.drl?.toUpperCase() || "—"}
          </span>
        </div>

        {/* DT */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>DT</span>
          <span style={{ fontSize: 13, color: dirColor(data.dt) }}>{dirArrow(data.dt)}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: dirColor(data.dt) }}>
            {data.dt?.toUpperCase() || "—"}
          </span>
        </div>

        {/* Pattern */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>PAT</span>
          <span style={{ fontSize: 13, color: dirColor(data.patternDir) }}>{dirArrow(data.patternDir)}</span>
        </div>

        {/* Leadership pill */}
        <span style={{
          fontFamily: MONO, fontSize: 8, fontWeight: 500,
          color: lColor, background: `${lColor}15`,
          border: `1px solid ${lColor}30`,
          borderRadius: 3, padding: "2px 7px",
        }}>
          {leaderLabel(data.leadership)}
        </span>

        {/* Timing */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: data.timingSync ? C.green : C.amber }} />
          <span style={{ fontFamily: MONO, fontSize: 8, color: data.timingSync ? C.green : C.amber }}>
            {data.timingSync ? "SYNCED" : "UNSYNCED"}
          </span>
        </div>

        {data.time && (
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginLeft: "auto" }}>
            {data.time}
          </span>
        )}
      </div>
    );
  }

  // ── Full layout ──────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "10px 16px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: C.blue }}>◈</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: "1.2px" }}>
            MODEL CONSENSUS
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Timing */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: data.timingSync ? C.green : C.amber }} />
            <span style={{ fontFamily: MONO, fontSize: 8, color: data.timingSync ? C.green : C.amber }}>
              {data.timingSync ? "TIMING SYNCED" : "TIMING UNSYNCED"}
            </span>
          </div>
          {/* Decision badge */}
          <span style={{
            fontFamily: MONO, fontSize: 8, fontWeight: 500,
            color: data.decision === "PROCEED" || data.decision === "BUY" ? C.green : C.muted,
            background: data.decision === "PROCEED" || data.decision === "BUY" ? `${C.green}15` : C.s3,
            border: `1px solid ${data.decision === "PROCEED" || data.decision === "BUY" ? `${C.green}30` : C.border}`,
            borderRadius: 3, padding: "1px 6px",
          }}>
            {data.decision}
          </span>
          {data.time && (
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>{data.time}</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Three model votes */}
        <div style={{ display: "flex", gap: 8 }}>
          <VoteTile label="DRL" dir={data.drl} sublabel="Reinforcement" />
          <VoteTile label="DT" dir={data.dt} sublabel="Decision Tree" />
          <VoteTile
            label="PATTERN"
            dir={data.patternDir}
            sublabel={data.patternName
              ? (data.patternName.length > 18 ? data.patternName.slice(0, 18) + "…" : data.patternName)
              : data.patternCount > 0 ? `${data.patternCount} detected` : "None detected"
            }
          />
        </div>

        {/* Leadership + consensus row */}
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {/* Leadership */}
          <div style={{
            flex: 1, background: C.s3, border: `1px solid ${lColor}30`,
            borderRadius: 8, padding: "8px 12px",
          }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginBottom: 4, letterSpacing: "0.8px" }}>
              MODEL LEADERSHIP
            </div>
            <div style={{ fontFamily: MONO, fontSize: 12, color: lColor, fontWeight: 500 }}>
              {leaderLabel(data.leadership)}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginTop: 3 }}>
              {data.leadership === "aligned" && "All models in agreement"}
              {data.leadership === "chaotic" && "Models diverge — lower reliability"}
              {data.leadership === "drl_leading" && "DRL model leading this call"}
              {data.leadership === "dt_leading" && "DT model leading this call"}
            </div>
          </div>

          {/* Consensus strength */}
          {data.consensusStrength != null && (
            <div style={{
              width: 80, background: C.s3, border: `1px solid ${cColor}30`,
              borderRadius: 8, padding: "8px 12px",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginBottom: 4, letterSpacing: "0.8px" }}>
                STRENGTH
              </div>
              <div style={{ fontFamily: MONO, fontSize: 18, color: cColor, fontWeight: 500, lineHeight: 1 }}>
                {Math.round(data.consensusStrength * 100)}%
              </div>
            </div>
          )}
        </div>

        {/* Evidence weights */}
        {data.weights && Object.keys(data.weights).length > 0 && (
          <div style={{
            background: C.s3, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: "10px 12px",
            display: "flex", flexDirection: "column", gap: 7,
          }}>
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: "0.8px" }}>
              EVIDENCE WEIGHTS
            </span>
            {Object.entries(data.weights)
              .filter(([, v]) => typeof v === "number")
              .sort(([, a], [, b]) => b - a)
              .slice(0, 5)
              .map(([key, val]) => (
                <WeightRow key={key} label={key.replace(/_/g, " ")} value={val} />
              ))
            }
          </div>
        )}

        {/* Rule votes dots */}
        {data.ruleVotes && <VoteDots votes={data.ruleVotes} />}

      </div>
    </div>
  );
}